import queryString from 'query-string';
import cookie from 'react-cookie';
import {
  RECENT_JOBS_COOKIE,
  PENDING_CREATIVE_SUBSCRIPTION_TX_STORAGE_KEY,
} from 'constants/constants';

/**
 * Persist checkout context before Stripe redirect (same-tab return only).
 * @param {object} opts
 * @param {number|string} opts.userId
 * @param {number} [opts.amount] — minor units (integer cents / pence)
 * @param {string} [opts.currency] — lowercase ISO code (e.g. usd, gbp)
 * @param {string} [opts.coupon_code] — Stripe coupon id when one is applied
 */
export function storePendingCreativeSubscriptionTransactionMetadata({
  userId,
  amount,
  currency,
  coupon_code,
}) {
  if (typeof sessionStorage === 'undefined' || userId == null) {
    return;
  }
  const payload = { userId };
  if (amount != null && amount !== '') {
    const n = Number(amount);
    if (Number.isFinite(n)) {
      payload.amount = Math.round(n);
    }
  }
  if (currency) {
    payload.currency = String(currency).toLowerCase();
  }
  if (coupon_code) {
    payload.coupon_code = String(coupon_code);
  }
  try {
    sessionStorage.setItem(
      PENDING_CREATIVE_SUBSCRIPTION_TX_STORAGE_KEY,
      JSON.stringify(payload)
    );
  } catch (e) {
    // private mode / quota
  }
}

export function clearPendingCreativeSubscriptionTransactionMetadata() {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(PENDING_CREATIVE_SUBSCRIPTION_TX_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * Numeric job id for subscription-transaction logging: explicit page id wins,
 * otherwise the most recent id from {@link RECENT_JOBS_COOKIE}.
 * @param {number|string|undefined|null} [explicitJobId] — e.g. `brief.id` from job details
 * @returns {number|null}
 */
export function resolveSubscriptionLogJobId(explicitJobId) {
  if (explicitJobId != null && explicitJobId !== '') {
    const n = Number(explicitJobId);
    if (Number.isFinite(n)) {
      return Math.round(n);
    }
  }
  const recentJobs = cookie.load(RECENT_JOBS_COOKIE) || [];
  const fromCookie = recentJobs[0]?.id;
  if (fromCookie != null && fromCookie !== '') {
    const n = Number(fromCookie);
    if (Number.isFinite(n)) {
      return Math.round(n);
    }
  }
  return null;
}

function consumePendingMetadataForUser(userId) {
  if (typeof sessionStorage === 'undefined' || userId == null) {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(
      PENDING_CREATIVE_SUBSCRIPTION_TX_STORAGE_KEY
    );
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (parsed.userId !== userId) {
      return {};
    }
    sessionStorage.removeItem(PENDING_CREATIVE_SUBSCRIPTION_TX_STORAGE_KEY);
    const { amount, currency, coupon_code } = parsed;
    const amountInt =
      amount != null && Number.isFinite(Number(amount))
        ? Math.round(Number(amount))
        : null;
    return {
      ...(amountInt != null && { amount: amountInt }),
      ...(currency != null && { currency }),
      ...(coupon_code != null && { coupon_code }),
    };
  } catch (e) {
    sessionStorage.removeItem(PENDING_CREATIVE_SUBSCRIPTION_TX_STORAGE_KEY);
    return {};
  }
}

/**
 * After Stripe creative checkout, log when the URL includes `success` and a
 * job id is available from `jobId` (job details page) or the recent-jobs cookie.
 * @param {number|string|undefined|null} [jobId] — prefer `brief.id` when known
 * @returns {boolean} true if a request was dispatched
 */
export function maybeLogSubscriptionTransaction({
  user,
  locationSearch,
  createSubscriptionTransaction,
  jobId,
}) {
  if (typeof window === 'undefined') return false;
  if (!user || user.id == null || !createSubscriptionTransaction) return false;

  const hasSuccess = new URLSearchParams(locationSearch).has('success');
  if (!hasSuccess) return false;

  if (jobId == null) {
    return false;
  }

  const extras = consumePendingMetadataForUser(user.id);
  createSubscriptionTransaction(user.id, { job_id: jobId, ...extras });
  return true;
}
